/* NOBL countdown declaration isolation v1 */
;(() => {
/**
 * Dawn Theme - Local Timezone Countdown Timer
 * Automatically counts down to user's local midnight and resets every 24 hours
 */

class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.initializeProperties();
    this.calculateNextLocalMidnight();
    this.observer = null;
  }

  initializeProperties() {
    // Get flip elements
    this.flips = {
      days: this.querySelector('countdown-timer-flip[type="days"]'),
      hours: this.querySelector('countdown-timer-flip[type="hours"]'),
      minutes: this.querySelector('countdown-timer-flip[type="minutes"]'),
      seconds: this.querySelector('countdown-timer-flip[type="seconds"]')
    };
    
    // Get display elements for days visibility control
    this.daysItem = this.querySelector('.countdown__timer-item--days');
    this.daysSeparator = this.querySelector('.countdown__timer-separator--days');
    
    this.interval = null;
    this.targetDate = null;
  }

  /**
   * Calculate next local midnight based on user's timezone
   * This is the core functionality from Prestige theme
   */
  calculateNextLocalMidnight() {
    const now = new Date();
    const nextMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0, 0, 0, 0
    );
    this.targetDate = nextMidnight.getTime();
  }

  initTimer() {
    // Ensure all flip elements are properly initialized
    this.ensureFlipElementsReady();
    
    // Update immediately, then start interval
    this.updateTimer();
    this.interval = setInterval(() => this.updateTimer(), 1000);
    
    // Add visibility change listener to pause/resume timer when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseTimer();
      } else {
        this.resumeTimer();
      }
    });
  }

  ensureFlipElementsReady() {
    // Wait for custom elements to be defined and initialized
    Object.keys(this.flips).forEach(type => {
      const element = this.flips[type];
      if (element && !element.updateValue) {
        // If updateValue method doesn't exist, define a simple fallback
        element.updateValue = function(value) {
          this.textContent = value.toString().padStart(2, '0');
        };
      }
    });
  }

  updateTimer() {
    const now = new Date().getTime();
    let remainingTime = this.targetDate - now;

    // If countdown reached zero, reset to next midnight
    if (remainingTime <= 0) {
      this.calculateNextLocalMidnight();
      remainingTime = this.targetDate - now;
      
      // Dispatch reset event
      this.dispatchEvent(new CustomEvent('countdown:reset', {
        detail: { 
          newTargetDate: this.targetDate
        }
      }));
    }

    // Calculate time units
    const timeUnits = this.calculateTimeUnits(Math.max(0, remainingTime));
    
    // Update display
    this.updateDisplay(timeUnits);
    this.toggleDaysVisibility(timeUnits.days);
    
    // Dispatch custom event for external listeners
    this.dispatchEvent(new CustomEvent('countdown:update', {
      detail: { 
        timeUnits, 
        remainingTime: Math.max(0, remainingTime)
      }
    }));
  }

  calculateTimeUnits(remainingTime) {
    const days = Math.floor(remainingTime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds };
  }

  updateDisplay({ days, hours, minutes, seconds }) {
    // Update each flip element if it exists and has updateValue method
    if (this.flips.days && typeof this.flips.days.updateValue === 'function') {
      this.flips.days.updateValue(days);
    } else if (this.flips.days) {
      this.flips.days.textContent = days.toString().padStart(2, '0');
    }
    
    if (this.flips.hours && typeof this.flips.hours.updateValue === 'function') {
      this.flips.hours.updateValue(hours);
    } else if (this.flips.hours) {
      this.flips.hours.textContent = hours.toString().padStart(2, '0');
    }
    
    if (this.flips.minutes && typeof this.flips.minutes.updateValue === 'function') {
      this.flips.minutes.updateValue(minutes);
    } else if (this.flips.minutes) {
      this.flips.minutes.textContent = minutes.toString().padStart(2, '0');
    }
    
    if (this.flips.seconds && typeof this.flips.seconds.updateValue === 'function') {
      this.flips.seconds.updateValue(seconds);
    } else if (this.flips.seconds) {
      this.flips.seconds.textContent = seconds.toString().padStart(2, '0');
    }
  }

  toggleDaysVisibility(days) {
    const shouldShowDays = days > 0;

    if (this.lastDaysVisibility === shouldShowDays) {
      return;
    }

    this.lastDaysVisibility = shouldShowDays;
    
    if (this.daysItem) {
      this.daysItem.style.display = shouldShowDays ? 'flex' : 'none';
      this.daysItem.classList.toggle('visible', shouldShowDays);
    }
    
    if (this.daysSeparator) {
      this.daysSeparator.style.display = shouldShowDays ? 'flex' : 'none';
      this.daysSeparator.classList.toggle('visible', shouldShowDays);
    }
  }

  pauseTimer() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  resumeTimer() {
    if (!this.interval) {
      this.interval = setInterval(() => this.updateTimer(), 1000);
    }
  }

  clearTimer() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  // Public API methods
  reset() {
    this.calculateNextLocalMidnight();
    this.updateTimer();
  }

  pause() {
    this.pauseTimer();
  }

  resume() {
    this.resumeTimer();
  }

  // Lifecycle callbacks
  connectedCallback() {
    this.setAttribute('role', 'timer');

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!this.interval) {
                this.initTimer();
              }

              this.observer.disconnect();
            }
          });
        },
        {
          rootMargin: '200px'
        }
      );

      this.observer.observe(this);
    } else {
      this.initTimer();
    }
  }

  disconnectedCallback() {
    this.clearTimer();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

class CountdownTimerFlip extends HTMLElement {
  constructor() {
    super();
    this.currentValue = '00';
    this.setupShadowDOM();
  }

  setupShadowDOM() {
    this.attachShadow({ mode: 'open' });
    
    // Create flip digits based on initial content or default to "00"
    const initialValue = this.textContent.trim() || '00';
    this.currentValue = initialValue.padStart(2, '0');
    
    const digits = [...this.currentValue].map((digit, index) => 
      `<countdown-timer-flip-digit 
         part="digit" 
         data-digit="${digit}"
         style="display: inline-block;">${digit}</countdown-timer-flip-digit>`
    );
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          font-variant-numeric: tabular-nums;
        }
        countdown-timer-flip-digit {
          display: inline-block;
        }
      </style>
      ${digits.join('')}
    `;
  }

  updateValue(value) {
    const newValue = Math.min(99, Math.max(0, value)).toString().padStart(2, '0');
    
    if (newValue !== this.currentValue) {
      this.currentValue = newValue;
      this.textContent = newValue;
      
      // Update shadow DOM digits
      const digits = this.shadowRoot.querySelectorAll('countdown-timer-flip-digit');
      [...newValue].forEach((digit, index) => {
        if (digits[index]) {
          digits[index].setAttribute('data-digit', digit);
          digits[index].textContent = digit;
        }
      });
    }
  }

  connectedCallback() {
    // Ensure we have proper initial setup
    if (!this.shadowRoot) {
      this.setupShadowDOM();
    }
  }
}

class CountdownTimerFlipDigit extends HTMLElement {
  static observedAttributes = ['data-digit'];
  
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          transition: transform 0.3s ease;
        }
        
        :host(.animating) {
          transform: rotateX(90deg);
        }
        
        @media (prefers-reduced-motion: reduce) {
          :host {
            transition: none;
          }
          
          :host(.animating) {
            transform: none;
          }
        }
        
        .digit {
          display: block;
        }
      </style>
      <span class="digit"><slot></slot></span>
    `;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-digit' && oldValue !== newValue && oldValue !== null) {
      this.textContent = newValue;
    } else if (name === 'data-digit') {
      this.textContent = newValue;
    }
  }
}

// Register custom elements with proper initialization order
if (!customElements.get('countdown-timer-flip-digit')) {
  customElements.define('countdown-timer-flip-digit', CountdownTimerFlipDigit);
}

if (!customElements.get('countdown-timer-flip')) {
  customElements.define('countdown-timer-flip', CountdownTimerFlip);
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CountdownTimer, CountdownTimerFlip, CountdownTimerFlipDigit };
}
})();
